import { API } from "aws-amplify";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { doGetOrderTicket } from "@goodfill/models-typescript";
import { OrderForm } from "../form/OrderForm";
import { Spinner } from "@goodfill/ui-components";
import { OrderFormState } from "../state/OrderFormState";
import { useRecoilValue } from "recoil";
import { OrderFormLayout } from "../OrderFormLayout";
import { OrderReceipt, OrderFormResult } from "../receipt/OrderReceipt";
import { UserAuthDataBalanceAccount } from "../../../backendApi/amplifyGraphQl/CustomGetUserData/UserData";
import { userAuthState } from "../../../../state";
import { PricedBond } from "../../../bonds/PricedBond";
import { OrderTicketDetailEntity } from "@goodfill/domain-entities";
import { OrderTicketId } from "@goodfill/domain-properties";
import { customerRequestOrderModify } from "@goodfill/backend-api-client";
import { backendFetcher } from "../../../backendApi/backendApi";
import { useRefreshUserDataHook } from "../../../useRefreshUserDataHook";
import { toastErrorHandler } from "../../../../unexpectedErrorHandler";
import { useLogout } from "../../../useLogout";

export const ModifyOrderContainer = (props: {
  moneyBalanceAccount: UserAuthDataBalanceAccount;
  orderTicketId: OrderTicketId;
  pricedBond: PricedBond;
  tradeDate: DateTime;
}) => {
  const orderTicketId = props.orderTicketId;

  const userData = useRecoilValue(userAuthState);
  const refreshUser = useRefreshUserDataHook();

  const [orderTicket, setOrderTicket] = useState<OrderTicketDetailEntity | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isModifyingOrder, setIsModifyingOrder] = useState(false);

  const [modifiedOrderResult, setModifiedOrderResult] = useState<OrderFormResult | null>(null);

  const logout = useLogout();

  useEffect(() => {
    let ignore = false;
    const init = async () => {
      if (ignore) {
        return;
      }
      setIsLoading(true);
      try {
        const result = await doGetOrderTicket(API, orderTicketId);
        if (ignore) {
          return;
        }
        setOrderTicket(result);
      } catch (e) {
        console.error(e);
        toast.error(`Error: ${e}`);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      ignore = true;
    };
  }, [orderTicketId]);

  const onSubmit = async (state: OrderFormState) => {
    if (userData === null || state.i.orderId === null || orderTicket === null) {
      return;
    }

    setIsModifyingOrder(true);

    await (
      await customerRequestOrderModify(
        backendFetcher,
        {
          orderId: state.i.orderId,
          bondPrice: state.getBasePrice(),
          faceAmount: state.s.faceAmount.amount,
        },
        userData.idToken,
      )
    ).match({
      success: async () => {
        await refreshUser();
        setModifiedOrderResult({ state, orderTicket });
        toast("Request to modify the order submitted");
      },
      businessMatcher: {},
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });

    setIsModifyingOrder(false);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (orderTicket === null) {
    return <>ERROR: OrderTicket to modify not loaded</>;
  }

  const initState = OrderFormState.createFromOrder(orderTicket, props.pricedBond, props.tradeDate);

  return (
    <OrderFormLayout pricedBond={props.pricedBond}>
      {modifiedOrderResult !== null ? (
        <OrderReceipt
          result={modifiedOrderResult}
          moneyBalanceAccount={props.moneyBalanceAccount}
          showCalculatedInfo={true}
        />
      ) : (
        <OrderForm
          isLoading={isModifyingOrder}
          moneyBalanceAccount={props.moneyBalanceAccount}
          initState={initState}
          onSubmit={onSubmit}
          availableToSell={null}
        />
      )}
    </OrderFormLayout>
  );
};
